import { INSTANCE as CloudAdapter } from '@/netadapters/CloudAdapter'
import type { ValidateClientParams } from '@/netadapters/CloudAdapter'
import { INSTANCE as UserInfoManager } from '@core/userInfo/UserInfoManager'
import { createSharedComposable } from '@vueuse/core'
import { INSTANCE as ToastManager } from '@/core/ToastManager'
import { getDomain } from '@/BuildUtils'
import { BrandName, BrandNameMap } from '@core/Constants'
import { ref } from 'vue'

const {
  VITE_HEYTAP_ENVIRONMENT,
  VITE_HEYTAP_SCRIPT_ID,
  VITE_SDK_APP_ID,
  VITE_SDK_BUSINESS_ID,
  VITE_SDK_MSP_BIZ_KEY,
  VITE_SDK_MSP_BIZ_SEC,
} = import.meta.env

const ACCOUNT_SDK_INIT_TIMEOUT_IN_MS = 100
const RETRY_TIMEOUT_FACTOR = 1.5
const RETRY_SDK_LIMIT = 3
const AccountSdkBaseConfig = {
  appId: VITE_SDK_APP_ID,
  businessId: VITE_SDK_BUSINESS_ID,
}

const windowAccount = () => {
  const AccountClient = window.account_web_sdk

  if (AccountClient) {
    const domain = getDomain(new URL(window.location.href).hostname)
    const domainKey = domain ? BrandNameMap.get(domain) : BrandName.HEYTAP

    return new AccountClient({
      environment: VITE_HEYTAP_ENVIRONMENT,
      // ignore原因：账号SDK TS supportBrand类型未定义清楚
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      brand: AccountClient.supportBrand[domainKey || BrandName.HEYTAP],
    })
  }
}

const useHeytapAccount = () => {
  let retry_sdk_times = 0
  const accountClientSdkInstance = ref(windowAccount())

  const initAccountClientSdk = () => {
    if (accountClientSdkInstance.value) {
      return
    }
    const accountSdkScript: HTMLElement | null = document.querySelector(`body script#${VITE_HEYTAP_SCRIPT_ID}`)

    if (accountSdkScript) {
      accountClientSdkInstance.value = windowAccount()

      if (!accountClientSdkInstance.value) {
        accountSdkScript.onload = () => {
          accountClientSdkInstance.value = windowAccount()

          return
        }
      }
    }
    // TODO: 没有accountSdkScript标签时，手动加入script并重新加载

    retrySdkInitialize()
  }

  const retrySdkInitialize = () => {
    accountClientSdkInstance.value = windowAccount()

    if (accountClientSdkInstance.value) {
      return
    }

    let timer

    if (retry_sdk_times <= RETRY_SDK_LIMIT) {
      timer = window.setTimeout(() => {
        retry_sdk_times++
        retrySdkInitialize()
      }, ACCOUNT_SDK_INIT_TIMEOUT_IN_MS * Math.pow(RETRY_TIMEOUT_FACTOR, retry_sdk_times))
    } else {
      timer && clearTimeout(timer)
    }
  }

  initAccountClientSdk()

  return { initAccountClientSdk, accountClientSdkInstance }
}

export const useSharedHeytapAccount = createSharedComposable(useHeytapAccount)

const useValidateClient = async (onCallback?: () => void) => {
  if (await UserInfoManager.isTrustedClient()) {
    // TODO: add UT for calling isTrustClient will send http request to backend api -- userInfo
    onCallback?.()

    return true
  }

  const { accountClientSdkInstance } = useSharedHeytapAccount()

  if (!accountClientSdkInstance.value) {
    ToastManager.show('owork_base.sdk_init_failed_tips')

    return
  }

  const userInfo = await UserInfoManager.getUserInfo()
  const ACCOUNT_AUTH_CONFIG = {
    ssoId: userInfo?.ssoId,
    mspBizK: VITE_SDK_MSP_BIZ_KEY,
    mspBizSec: VITE_SDK_MSP_BIZ_SEC,
    ...AccountSdkBaseConfig,
  }

  accountClientSdkInstance.value.auth({
    ...ACCOUNT_AUTH_CONFIG,
    onSuccess(ticketNo?: string) {
      if (!ticketNo) {
        return false
      }

      const param: ValidateClientParams = {
        ...AccountSdkBaseConfig,
        ticketNo,
      }

      CloudAdapter.isCertificatedClient(param).then((res) => {
        if (res) {
          UserInfoManager.initUserInfo()
          onCallback?.()

          return true
        } else {
          return false
        }
      })
    },
  })
}

export default useValidateClient
